import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import {
  authAPI,
  authUtils,
  type User,
  type Company,
  type LoginCredentials,
  type RegisterData,
} from '../services/auth';
import { useNavigate, useLocation } from 'react-router-dom';

interface AuthContextType {
  user: User | undefined;
  company: any;
  isAuthenticated: boolean;
  isLoadingUser: boolean;
  isLoadingCompany: boolean;
  login: (credentials: LoginCredentials) => void;
  register: (data: RegisterData) => void;
  registerMutation: UseMutationResult<any, Error, RegisterData, unknown>;
  logout: () => void;
  updateProfile: (userData: Partial<User>) => void;
  updateCompany: (companyData: Partial<Company>) => void;
  changePassword: (currentPassword: string, newPassword: string) => void;
  forgotPassword: (email: string) => void;
  resetPassword: (token: string, newPassword: string) => void;
  refreshToken: () => void;
  isLoggingIn: boolean;
  isRegistering: boolean;
  isLoggingOut: boolean;
  isUpdatingProfile: boolean;
  isUpdatingCompany: boolean;
  isChangingPassword: boolean;
  isSendingForgotPassword: boolean;
  isResettingPassword: boolean;
  isRefreshingToken: boolean;
  loginError: any;
  registerError: any;
  logoutError: any;
  updateProfileError: any;
  updateCompanyError: any;
  changePasswordError: any;
  forgotPasswordError: any;
  resetPasswordError: any;
  refreshTokenError: any;
  resetLoginError: () => void;
  resetRegisterError: () => void;
  resetLogoutError: () => void;
  resetProfileUpdateError: () => void;
  resetCompanyUpdateError: () => void;
  resetPasswordChangeError: () => void;
  resetForgotPasswordError: () => void;
  resetResetPasswordError: () => void;
  resetRefreshTokenError: () => void;
}

export function useAuth(): AuthContextType {
  // Expose the register mutation directly
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();

  // Get current user query
  const {
    data: user,
    isLoading: isLoadingUser,
    // error: userError,
  } = useQuery({
    queryKey: ['user'],
    queryFn: authAPI.getCurrentUser,
    enabled: authUtils.isAuthenticated(),
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Get company data
  const { data: company, isLoading: isLoadingCompany } = useQuery({
    queryKey: ['company'],
    queryFn: () => {
      const authData = authUtils.getAuthData();
      return authData?.company || null;
    },
    enabled: authUtils.isAuthenticated(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Login mutation
  const loginMutation = useMutation({
    mutationFn: authAPI.login,
    onSuccess: (data) => {
      authUtils.saveAuthData(data);
      queryClient.setQueryData(['user'], data.user);
      queryClient.setQueryData(['company'], data.company);

      // Redirect to previous page if available, otherwise to dashboard
      const from = (location.state as { from?: string })?.from || '/dashboard';
      navigate(from, { replace: true });
    },
    onError: (error) => {
      console.error('Login failed:', error);
    },
  });

  // Register mutation
  const registerMutation = useMutation({
    mutationFn: authAPI.register,
    onSuccess: (data) => {
      authUtils.saveAuthData(data);
      queryClient.setQueryData(['user'], data.user);
      queryClient.setQueryData(['company'], data.company);
    },
    onError: (error) => {
      console.error('Registration failed:', error);
    },
  });

  // Logout mutation
  const logoutMutation = useMutation({
    mutationFn: authAPI.logout,
    onSuccess: () => {
      // Clear all auth data
      authUtils.clearAuthData();

      // Clear all React Query cache
      queryClient.clear();

      // Navigate to sign-in page
      navigate('/sign-in');
    },
    onError: (error) => {
      console.error('Logout failed:', error);
      // Even if logout fails on server, clear local data
      authUtils.clearAuthData();
      queryClient.clear();
      navigate('/sign-in');
    },
  });

  // Update profile mutation
  const updateProfileMutation = useMutation({
    mutationFn: authAPI.updateProfile,
    onSuccess: (updatedUser) => {
      // Update React Query cache
      queryClient.setQueryData(['user'], updatedUser);

      // Update cookies
      const authData = authUtils.getAuthData();
      if (authData) {
        authUtils.saveAuthData({
          user: updatedUser,
          company: authData.company,
          accessToken: authData.accessToken,
          refreshToken: authData.refreshToken || authData.accessToken,
        });
      }
    },
    onError: (error) => {
      console.error('Profile update failed:', error);
    },
  });

  // Update company mutation
  const updateCompanyMutation = useMutation({
    mutationFn: authAPI.updateCompany,
    onSuccess: (updatedCompany) => {
      // Update React Query cache
      queryClient.setQueryData(['company'], updatedCompany);

      // Update cookies
      const authData = authUtils.getAuthData();
      if (authData && authData.user && authData.accessToken) {
        authUtils.saveAuthData({
          user: authData.user,
          company: updatedCompany,
          accessToken: authData.accessToken,
          refreshToken: authData.refreshToken || authData.accessToken,
        });
      }
    },
    onError: (error) => {
      console.error('Company update failed:', error);
    },
  });

  // Change password mutation
  const changePasswordMutation = useMutation({
    mutationFn: ({
      currentPassword,
      newPassword,
    }: {
      currentPassword: string;
      newPassword: string;
    }) => authAPI.changePassword(currentPassword, newPassword),
    onSuccess: () => {
      console.log('Password changed successfully');
    },
    onError: (error) => {
      console.error('Password change failed:', error);
    },
  });

  // Forgot password mutation
  const forgotPasswordMutation = useMutation({
    mutationFn: authAPI.forgotPassword,
    onSuccess: () => {
      console.log('Password reset email sent');
    },
    onError: (error) => {
      console.error('Forgot password failed:', error);
    },
  });

  // Reset password mutation
  const resetPasswordMutation = useMutation({
    mutationFn: ({
      token,
      newPassword,
    }: {
      token: string;
      newPassword: string;
    }) => authAPI.resetPassword(token, newPassword),
    onSuccess: () => {
      console.log('Password reset successfully');
    },
    onError: (error) => {
      console.error('Password reset failed:', error);
    },
  });

  // Refresh token mutation
  const refreshTokenMutation = useMutation({
    mutationFn: authAPI.refreshToken,
    onSuccess: (data) => {
      // Update auth data with new tokens
      const authData = authUtils.getAuthData();
      if (authData) {
        authUtils.saveAuthData({
          ...authData,
          accessToken: data.accessToken,
          refreshToken: data.refreshToken,
        });
      }
      console.log('Token refreshed successfully');
    },
    onError: (error) => {
      console.error('Token refresh failed:', error);
      // If refresh fails, logout user
      authUtils.clearAuthData();
      queryClient.clear();
      navigate('/sign-in');
    },
  });

  // Manual login function
  const login = (credentials: LoginCredentials) => {
    loginMutation.mutate(credentials);
  };

  // Manual register function
  const register = (data: RegisterData) => {
    registerMutation.mutate(data);
  };

  // Manual logout function
  const logout = () => {
    logoutMutation.mutate();
  };

  // Manual update profile function
  const updateProfile = (userData: Partial<User>) => {
    updateProfileMutation.mutate(userData);
  };

  // Manual update company function
  const updateCompany = (companyData: Partial<Company>) => {
    updateCompanyMutation.mutate(companyData);
  };

  // Manual change password function
  const changePassword = (currentPassword: string, newPassword: string) => {
    changePasswordMutation.mutate({ currentPassword, newPassword });
  };

  // Manual forgot password function
  const forgotPassword = (email: string) => {
    forgotPasswordMutation.mutate(email);
  };

  // Manual reset password function
  const resetPassword = (token: string, newPassword: string) => {
    resetPasswordMutation.mutate({ token, newPassword });
  };

  // Manual refresh token function
  const refreshToken = () => {
    refreshTokenMutation.mutate();
  };

  return {
    // User data
    user,
    company,
    isAuthenticated: authUtils.isAuthenticated(),

    // Loading states
    isLoadingUser,
    isLoadingCompany,

    // Login/Register/Logout functions
    login,
    register,
    logout,

    // Expose the register mutation directly for access to its properties
    registerMutation,

    // Profile management functions
    updateProfile,
    updateCompany,
    changePassword,

    // Password recovery functions
    forgotPassword,
    resetPassword,

    // Token management
    refreshToken,

    // Mutation loading states
    isLoggingIn: loginMutation.isPending,
    isRegistering: registerMutation.isPending,
    isLoggingOut: logoutMutation.isPending,
    isUpdatingProfile: updateProfileMutation.isPending,
    isUpdatingCompany: updateCompanyMutation.isPending,
    isChangingPassword: changePasswordMutation.isPending,
    isSendingForgotPassword: forgotPasswordMutation.isPending,
    isResettingPassword: resetPasswordMutation.isPending,
    isRefreshingToken: refreshTokenMutation.isPending,

    // Error states
    loginError: loginMutation.error,
    registerError: registerMutation.error,
    logoutError: logoutMutation.error,
    updateProfileError: updateProfileMutation.error,
    updateCompanyError: updateCompanyMutation.error,
    changePasswordError: changePasswordMutation.error,
    forgotPasswordError: forgotPasswordMutation.error,
    resetPasswordError: resetPasswordMutation.error,
    refreshTokenError: refreshTokenMutation.error,

    // Reset functions
    resetLoginError: () => loginMutation.reset(),
    resetRegisterError: () => registerMutation.reset(),
    resetLogoutError: () => logoutMutation.reset(),
    resetProfileUpdateError: () => updateProfileMutation.reset(),
    resetCompanyUpdateError: () => updateCompanyMutation.reset(),
    resetPasswordChangeError: () => changePasswordMutation.reset(),
    resetForgotPasswordError: () => forgotPasswordMutation.reset(),
    resetResetPasswordError: () => resetPasswordMutation.reset(),
    resetRefreshTokenError: () => refreshTokenMutation.reset(),
  };
}
